import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Paperclip, Plus, Eye, Download, Trash2, FileText, Image, FileVideo, File } from "lucide-react";
import type { AttachmentEntry } from "@/domain/rdo/schema";

type AttachmentsProps = {
  value: AttachmentEntry[];
  onChange: (v: AttachmentEntry[]) => void;
  mode: "create" | "edit" | "view";
};

const getFileIcon = (mime: string) => {
  if (mime.startsWith('image/')) {
    return <Image className="w-4 h-4 text-green-500" />;
  }
  if (mime.startsWith('video/')) {
    return <FileVideo className="w-4 h-4 text-blue-500" />;
  }
  if (mime === 'application/pdf') {
    return <FileText className="w-4 h-4 text-red-500" />;
  }
  return <File className="w-4 h-4 text-gray-500" />;
};

const formatFileSize = (bytes?: number | string): string => {
  if (bytes == null || bytes === "") return "Tamanho desconhecido";
  // Formato real persiste o tamanho já como string (ex.: "0.12 MB")
  if (typeof bytes === "string") return bytes;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isPreviewable = (mime: string): boolean => {
  return mime.startsWith('image/') || mime === 'application/pdf';
};

export function AttachmentsModule({ value, onChange, mode }: AttachmentsProps) {
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);
  const [previewFile, setPreviewFile] = useState<AttachmentEntry | null>(null);
  const { toast } = useToast();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const maxSize = 10 * 1024 * 1024; // 10MB limit for attachments
    const newAttachments: AttachmentEntry[] = [];

    Array.from(files).forEach((file) => {
      if (file.size > maxSize) {
        toast({
          title: "Arquivo muito grande",
          description: `${file.name} excede o limite de 10MB`,
          variant: "destructive",
        });
        return;
      }

      const url = URL.createObjectURL(file);
      const newAttachment: AttachmentEntry = {
        id: crypto.randomUUID(),
        name: file.name,
        mime: file.type || "application/octet-stream",
        size: file.size,
        url: url,
        uploadedBy: "Usuário Atual", // TODO: Get from auth context
        createdAt: new Date().toISOString(),
      };

      newAttachments.push(newAttachment);
    });

    if (newAttachments.length > 0) {
      onChange([...value, ...newAttachments]);
      toast({
        title: "Anexos adicionados",
        description: `${newAttachments.length} arquivo(s) adicionado(s) com sucesso`,
      });
    }

    // Reset input
    event.target.value = "";
  };

  const handleDelete = (index: number) => {
    const newAttachments = value.filter((_, i) => i !== index);
    onChange(newAttachments);
    setDeleteConfirmIndex(null);
    toast({
      title: "Anexo removido",
      description: "O anexo foi removido com sucesso",
    });
  };

  const handlePreview = (attachment: AttachmentEntry) => {
    if (isPreviewable(attachment.mime)) {
      setPreviewFile(attachment);
    } else {
      toast({
        title: "Visualização não disponível",
        description: "Este tipo de arquivo não pode ser visualizado no navegador",
        variant: "destructive",
      });
    }
  };

  const handleCopyLink = (attachment: AttachmentEntry) => {
    if (attachment.url) {
      navigator.clipboard.writeText(attachment.url);
      toast({
        title: "Link copiado",
        description: "O link do anexo foi copiado para a área de transferência",
      });
    }
  };

  return (
    <>
      <Card data-section="attachments">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Paperclip className="w-5 h-5" />
            Anexos ({value.length})
            {mode !== "view" && (
              <label htmlFor="attachment-upload">
                <Button size="sm" className="ml-2" asChild>
                  <span>
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Anexos
                  </span>
                </Button>
                <input
                  id="attachment-upload"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                  accept="*/*"
                />
              </label>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {value.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Paperclip className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum anexo adicionado</p>
            </div>
          ) : (
            <div className="space-y-3">
              {value.map((attachment, index) => (
                <div
                  key={attachment.id || index}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {getFileIcon(attachment.mime)}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{attachment.name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{formatFileSize(attachment.size)}</span>
                        {attachment.uploadedBy && (
                          <>
                            <span>•</span>
                            <span>{attachment.uploadedBy}</span>
                          </>
                        )}
                        {attachment.createdAt && (
                          <>
                            <span>•</span>
                            <span>{new Date(attachment.createdAt).toLocaleDateString('pt-BR')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {isPreviewable(attachment.mime) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handlePreview(attachment)}
                        title="Visualizar"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    {attachment.url && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCopyLink(attachment)}
                        title="Copiar link"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                    {mode !== "view" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleteConfirmIndex(index)}
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog 
        open={deleteConfirmIndex !== null} 
        onOpenChange={() => setDeleteConfirmIndex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este anexo? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleteConfirmIndex !== null && handleDelete(deleteConfirmIndex)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      {previewFile && (
        <AlertDialog open={!!previewFile} onOpenChange={() => setPreviewFile(null)}>
          <AlertDialogContent className="max-w-4xl max-h-[80vh] overflow-auto">
            <AlertDialogHeader>
              <AlertDialogTitle>{previewFile.name}</AlertDialogTitle>
            </AlertDialogHeader>
            <div className="mt-4">
              {previewFile.mime.startsWith('image/') ? (
                <img 
                  src={previewFile.url} 
                  alt={previewFile.name}
                  className="max-w-full h-auto rounded-lg"
                />
              ) : previewFile.mime === 'application/pdf' ? (
                <iframe
                  src={previewFile.url}
                  className="w-full h-[60vh] rounded-lg border"
                  title={previewFile.name}
                />
              ) : null}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Fechar</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}